import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Copy, Download, KeyRound, ShieldCheck, ShieldOff } from 'lucide-react';
import {
  confirmar2FA,
  desativar2FA,
  getMe,
  iniciar2FA,
  regerarCodigosRecuperacao,
} from '@/lib/api';
import { translateError } from '@/lib/errors';
import { toast } from '@/lib/toast';
import { useAuthStore } from '@/stores/authStore';

type Etapa =
  | { tipo: 'inicio' }
  | { tipo: 'senha' }
  | { tipo: 'qr'; segredo: string; uri: string }
  | { tipo: 'codigos'; codigos: string[] };

const campo = 'w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm outline-none focus:border-vyxora-cyan-400';

/**
 * Verificação em duas etapas da própria conta: ativar (senha → QR → código → códigos de
 * recuperação), gerar novos códigos e desativar. `obrigatorio` = tela forçada do administrador.
 */
export function DoisFatoresPainel({ obrigatorio = false }: { obrigatorio?: boolean }) {
  const { t } = useTranslation('app');
  const user = useAuthStore((s) => s.user);
  const [etapa, setEtapa] = useState<Etapa>({ tipo: 'inicio' });

  if (!user) return null;

  if (etapa.tipo === 'codigos') {
    return (
      <CodigosRecuperacao
        codigos={etapa.codigos}
        onConcluir={async () => {
          // O usuário da sessão só é atualizado aqui: na tela obrigatória do admin, atualizar antes
          // desmontaria o painel e os códigos sumiriam antes de serem guardados.
          useAuthStore.getState().setUser(await getMe());
          setEtapa({ tipo: 'inicio' });
        }}
      />
    );
  }

  if (user.doisFAAtivo && etapa.tipo === 'inicio') {
    return <Ativo obrigatorio={user.doisFAObrigatorio} onNovosCodigos={(codigos) => setEtapa({ tipo: 'codigos', codigos })} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <ShieldOff className="mt-0.5 h-5 w-5 shrink-0 text-yellow-300" />
        <p className="text-sm text-vyxora-text-light/80">
          {obrigatorio ? t('doisFatores.descricaoObrigatorio') : t('doisFatores.descricao')}
        </p>
      </div>

      {etapa.tipo === 'inicio' && (
        <button onClick={() => setEtapa({ tipo: 'senha' })} className="btn-primary text-sm">
          <ShieldCheck className="h-4 w-4" /> {t('doisFatores.ativar')}
        </button>
      )}

      {etapa.tipo === 'senha' && (
        <PedirSenha
          onOk={async (senha) => {
            const r = await iniciar2FA(senha);
            setEtapa({ tipo: 'qr', segredo: r.segredo, uri: r.uriOtpAuth });
          }}
          onCancelar={() => setEtapa({ tipo: 'inicio' })}
        />
      )}

      {etapa.tipo === 'qr' && (
        <LerQr
          segredo={etapa.segredo}
          uri={etapa.uri}
          onConfirmado={(codigos) => setEtapa({ tipo: 'codigos', codigos })}
        />
      )}
    </div>
  );
}

function PedirSenha({ onOk, onCancelar }: { onOk: (senha: string) => Promise<void>; onCancelar: () => void }) {
  const { t } = useTranslation('app');
  const [senha, setSenha] = useState('');
  const [enviando, setEnviando] = useState(false);
  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        setEnviando(true);
        try {
          await onOk(senha);
        } catch (err) {
          toast.error(translateError(err));
        } finally {
          setEnviando(false);
        }
      }}
      className="max-w-sm space-y-3"
    >
      <label className="block text-sm">
        {t('doisFatores.confirmeSenha')}
        <input type="password" autoComplete="current-password" value={senha} onChange={(e) => setSenha(e.target.value)} className={`mt-1 ${campo}`} required />
      </label>
      <div className="flex gap-2">
        <button type="submit" disabled={enviando} className="btn-primary text-sm disabled:opacity-50">{t('doisFatores.continuar')}</button>
        <button type="button" onClick={onCancelar} className="btn-secondary text-sm">{t('comum.cancelar')}</button>
      </div>
    </form>
  );
}

function LerQr({ segredo, uri, onConfirmado }: { segredo: string; uri: string; onConfirmado: (codigos: string[]) => void }) {
  const { t } = useTranslation('app');
  const [svg, setSvg] = useState<string | null>(null);
  const [codigo, setCodigo] = useState('');
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    let ativo = true;
    // Gerado no navegador: o segredo nunca vai para um serviço de QR de terceiros.
    // Import dinâmico: o pacote fica fora do bundle e do pré-render SSG.
    import('qrcode')
      .then((QR) => QR.toString(uri, { type: 'svg', margin: 1, width: 192, errorCorrectionLevel: 'M' }))
      .then((s) => ativo && setSvg(s))
      .catch(() => ativo && setSvg(null));
    return () => {
      ativo = false;
    };
  }, [uri]);

  return (
    <div className="space-y-4">
      <ol className="list-decimal space-y-1 pl-5 text-sm text-vyxora-text-light/80">
        <li>{t('doisFatores.qr.passo1')}</li>
        <li>{t('doisFatores.qr.passo2')}</li>
        <li>{t('doisFatores.qr.passo3')}</li>
      </ol>
      <div className="flex flex-wrap items-center gap-6">
        <div className="flex h-48 w-48 items-center justify-center rounded-lg bg-white p-2" aria-label={t('doisFatores.qr.rotuloQr')}>
          {svg ? <div className="h-full w-full [&>svg]:h-full [&>svg]:w-full" dangerouslySetInnerHTML={{ __html: svg }} /> : <span className="text-xs text-black/50">{t('doisFatores.qr.gerando')}</span>}
        </div>
        <div className="min-w-0 text-sm">
          <p className="text-vyxora-text-light/60">{t('doisFatores.qr.chave')}</p>
          <code className="mt-1 block break-all rounded bg-white/5 px-2 py-1 font-mono tracking-wider">{segredo.match(/.{1,4}/g)?.join(' ')}</code>
          <a href={uri} className="mt-2 inline-block text-xs text-vyxora-accent hover:underline">{t('doisFatores.qr.abrirNoApp')}</a>
        </div>
      </div>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          const rt = useAuthStore.getState().refreshToken;
          if (!rt) return;
          setEnviando(true);
          try {
            const r = await confirmar2FA(codigo, rt);
            // Tokens já trocados: a sessão passa a ter MFA e as demais sessões foram encerradas.
            useAuthStore.getState().setTokens(r.tokens);
            onConfirmado(r.codigosRecuperacao);
          } catch (err) {
            toast.error(translateError(err));
          } finally {
            setEnviando(false);
          }
        }}
        className="flex max-w-sm items-end gap-2"
      >
        <label className="flex-1 text-sm">
          {t('doisFatores.qr.codigo')}
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9 ]{6,7}"
            maxLength={7}
            value={codigo}
            onChange={(e) => setCodigo(e.target.value)}
            className={`mt-1 font-mono tracking-widest ${campo}`}
            required
          />
        </label>
        <button type="submit" disabled={enviando} className="btn-primary text-sm disabled:opacity-50">{t('doisFatores.qr.ativar')}</button>
      </form>
    </div>
  );
}

function CodigosRecuperacao({ codigos, onConcluir }: { codigos: string[]; onConcluir: () => void }) {
  const { t } = useTranslation('app');
  const texto = `${t('doisFatores.codigos.cabecalhoArquivo')}\n\n${codigos.join('\n')}\n`;
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <KeyRound className="mt-0.5 h-5 w-5 shrink-0 text-vyxora-accent" />
        <p className="text-sm text-vyxora-text-light/80">
          <Trans t={t} i18nKey="doisFatores.codigos.guarde" components={{ strong: <strong /> }} />
        </p>
      </div>
      <ul className="grid max-w-md grid-cols-2 gap-2 font-mono text-sm">
        {codigos.map((c) => (
          <li key={c} className="rounded bg-white/5 px-3 py-1.5 text-center tracking-wider">{c}</li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() =>
            navigator.clipboard.writeText(texto).then(
              () => toast.success(t('doisFatores.codigos.copiados')),
              () => toast.error(t('doisFatores.codigos.erroCopiar')),
            )
          }
          className="btn-secondary text-sm"
        >
          <Copy className="h-4 w-4" /> {t('doisFatores.codigos.copiar')}
        </button>
        <a
          href={`data:text/plain;charset=utf-8,${encodeURIComponent(texto)}`}
          download={t('doisFatores.codigos.nomeArquivo')}
          className="btn-secondary text-sm"
        >
          <Download className="h-4 w-4" /> {t('doisFatores.codigos.baixar')}
        </a>
        <button onClick={onConcluir} className="btn-primary text-sm">{t('doisFatores.codigos.concluir')}</button>
      </div>
    </div>
  );
}

function Ativo({ obrigatorio, onNovosCodigos }: { obrigatorio: boolean; onNovosCodigos: (c: string[]) => void }) {
  const { t } = useTranslation('app');
  const [acao, setAcao] = useState<'nenhuma' | 'codigos' | 'desativar'>('nenhuma');
  const [senha, setSenha] = useState('');
  const [codigo, setCodigo] = useState('');
  const [enviando, setEnviando] = useState(false);

  async function executar() {
    setEnviando(true);
    try {
      if (acao === 'codigos') {
        const r = await regerarCodigosRecuperacao(codigo);
        onNovosCodigos(r.codigosRecuperacao);
      } else {
        await desativar2FA(senha, codigo);
        useAuthStore.getState().setUser(await getMe());
        toast.success(t('doisFatores.ativo.desativado'));
      }
      setAcao('nenhuma');
    } catch (err) {
      toast.error(translateError(err));
    } finally {
      setEnviando(false);
      setCodigo('');
      setSenha('');
    }
  }

  return (
    <div className="space-y-4">
      <p className="flex items-center gap-2 text-sm text-vyxora-success">
        <ShieldCheck className="h-5 w-5" /> {t('doisFatores.ativo.status')}
      </p>
      {acao === 'nenhuma' ? (
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setAcao('codigos')} className="btn-secondary text-sm">{t('doisFatores.ativo.gerarCodigos')}</button>
          {obrigatorio ? (
            <span className="self-center text-xs text-vyxora-text-light/50">{t('doisFatores.ativo.obrigatoria')}</span>
          ) : (
            <button onClick={() => setAcao('desativar')} className="btn-secondary text-sm border-red-500/40 text-red-300 hover:bg-red-500/10">{t('doisFatores.ativo.desativar')}</button>
          )}
        </div>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); void executar(); }} className="max-w-sm space-y-3">
          {acao === 'desativar' && (
            <label className="block text-sm">
              {t('doisFatores.ativo.senha')}
              <input type="password" autoComplete="current-password" value={senha} onChange={(e) => setSenha(e.target.value)} className={`mt-1 ${campo}`} required />
            </label>
          )}
          <label className="block text-sm">
            {acao === 'desativar' ? t('doisFatores.ativo.codigoAppOuRecuperacao') : t('doisFatores.ativo.codigoApp')}
            <input autoComplete="one-time-code" value={codigo} onChange={(e) => setCodigo(e.target.value)} className={`mt-1 font-mono ${campo}`} required />
          </label>
          {acao === 'codigos' && (
            <p className="text-xs text-vyxora-text-light/50">{t('doisFatores.ativo.codigosDeixamDeValer')}</p>
          )}
          <div className="flex gap-2">
            <button type="submit" disabled={enviando} className="btn-primary text-sm disabled:opacity-50">
              {acao === 'codigos' ? t('doisFatores.ativo.gerar') : t('doisFatores.ativo.desativar')}
            </button>
            <button type="button" onClick={() => setAcao('nenhuma')} className="btn-secondary text-sm">{t('comum.cancelar')}</button>
          </div>
        </form>
      )}
    </div>
  );
}
